// TECH DEBT: This lives here so the bulk-deploy reconciliation (api layer) and
// deployment (app layer) can share one concurrency-capped runner. It is intended
// to move alongside the Action Proxy work and should ideally not live long.

/**
 * Map over `items` running at most `limit` async `iteratee` calls at a time.
 *
 * Uses a rolling worker pool: `limit` workers each pull the next unclaimed item,
 * so a freed worker immediately picks up more work rather than waiting for a whole
 * batch to drain. Results preserve input order (like `Promise.all`), and a rejected
 * iteratee rejects the returned promise (like `Promise.all`) once it settles.
 *
 * @param {Array} items        Items to process.
 * @param {number} limit       Max iteratee calls in flight at once (clamped to >= 1).
 * @param {Function} iteratee  async (item, index) => result
 * @param {Function} [onSettled] Called with the cumulative settled count after each
 *                               item settles (resolve or reject) — for progress UIs.
 * @returns {Promise<Array>}    Results in input order.
 */
export default async function mapWithConcurrency(items, limit, iteratee, onSettled) {
  const results = new Array(items.length);
  let cursor = 0;
  let completed = 0;

  async function worker() {
    while (cursor < items.length) {
      const index = cursor;
      cursor += 1;

      try {
        results[index] = await iteratee(items[index], index);
      } finally {
        completed += 1;
        onSettled?.(completed);
      }
    }
  }

  const workerCount = Math.min(Math.max(1, limit), items.length);
  await Promise.all(Array.from({ length: workerCount }, () => worker()));

  return results;
}
